/**
 * @flow
 */

import React from 'react';
import gql from 'graphql-tag';
import uuid from 'uuid';
import { useForm, Controller } from 'react-hook-form';
import { useMutation } from '@apollo/client';
import { useSelector, useDispatch } from 'react-redux';
import { some, get, find } from 'lodash';
import { Form, Icon, Input, Message, Label as BaseLabel } from 'semantic-ui-react';
import { injectIntl, FormattedMessage, defineMessages } from 'react-intl';

import { Modal, Button } from '../../../components';
import { setIsOwner, switchSalon } from '../../../actions/user';
import withNewSalon from '../../../lib/withNewSalon';

import { GET_SALONS_QUERY } from '../containers/SalonSelect';
import { GET_SALON_LIST_QUERY } from '../../../pages/salon-list/components/SalonList';

import type { ModalDialog, Intl } from '../../../type';

type CreateSalonDialogProps = {
    intl: Intl,
    setupNewSalon: Function,
    setOwnerAsMaster: Function,
} & ModalDialog;

// The mutation to create a new salon
export const CREATE_SALON_MUTATION = gql`
    mutation createSalon($input: CreateSalonInput!) {
        createSalon(input: $input) {
            salon {
                id
                name
                address
                administrator {
                    id
                    firstName
                    lastName
                    phone
                    email
                }
                masterCount
                settings {
                    schedule {
                        startAt
                        endAt
                        step
                        format
                    }
                    enableArchive
                }
                subscription {
                    active
                    subId
                    customerId
                    priceId
                    trialEndsAt
                }
            }
        }
    }
`;

const intlMessages = defineMessages({
    salonNamePlaceholder: {
        id: 'navbar.menu.newSalon.salonName',
        defaultMessage: 'Salon name',
    },
    salonAddressPlaceholder: {
        id: 'navbar.menu.newSalon.salonAddress',
        defaultMessage: 'Salon address',
    },
    salonNameError: {
        id: 'navbar.menu.newSalon.salonNameError',
        defaultMessage: 'Enter salon name',
    },
    salonAddressError: {
        id: 'navbar.menu.newSalon.salonAddressError',
        defaultMessage: 'Enter salon address',
    },
    nameExistingsError: {
        id: 'pages.settings.generalSettings.nameExistingsError',
        defaultMessage: 'Salon with the same name already exists',
    },
    defaultErrorMessage: {
        id: 'errors.defaultMessage',
        defaultMessage: 'Something went wrong',
    },
});

const CreateSalonDialog = ({
    trigger,
    open,
    onClose,
    intl,
    setupNewSalon,
    setOwnerAsMaster,
}: CreateSalonDialogProps) => {
    const user = useSelector(({ user: u }) => u.get('id'));
    const userData = useSelector(({ user: u }) => u.get('userData'));
    const dispatch = useDispatch();

    const {
        control,
        handleSubmit,
        setError,
        formState: { isSubmitting, isDirty, errors },
    } = useForm({ defaultValues: { name: '', address: '' } });

    const [createSalon] = useMutation(CREATE_SALON_MUTATION);

    const onSubmit = async formData => {
        try {
            const { data: { createSalon: result } } = await createSalon({
                variables: { input: formData },
                optimisticResponse: {
                    __typename: 'Mutation',
                    createSalon: {
                        __typename: 'CreateSalonPayload',
                        salon: {
                            __typename: 'Salon',
                            id: uuid.v4(),
                            name: formData.name,
                            address: formData.address,
                            administrator: {
                                __typename: 'Administrator',
                                id: user,
                                ...userData,
                            },
                            masterCount: 4,
                            settings: {
                                __typename: 'Settings',
                                schedule: {
                                    __typename: 'Schedule',
                                    startAt: 480,
                                    endAt: 1200,
                                    step: 30,
                                    format: 'HH:mm',
                                },
                                enableArchive: false,
                            },
                        },
                    },
                },
                update: (store, { data: { createSalon: payload } }) => {
                    const data = store.readQuery({ query: GET_SALONS_QUERY });
                    if (data && data.viewer && data.viewer.salons) {
                        store.writeQuery({
                            query: GET_SALONS_QUERY,
                            data: {
                                ...data,
                                viewer: {
                                    ...data.viewer,
                                    salons: [
                                        ...data.viewer.salons,
                                        payload.salon,
                                    ],
                                },
                            },
                        });
                    }

                    const inCache = some(store.watches, item =>
                        find(
                            get(item, 'query.definitions', []),
                            def =>
                                get(def, 'name.value', '') === 'getSalonList',
                        ),
                    );

                    if (inCache) {
                        const salonList = store.readQuery({
                            query: GET_SALON_LIST_QUERY,
                            variables: { resolveRole: 'SYSADMIN' },
                        });

                        if (
                            salonList &&
                            salonList.viewer &&
                            salonList.viewer.salonList
                        ) {
                            const newSalon = {
                                ...payload.salon,
                                masterCount: 4,
                            };
                            store.writeQuery({
                                query: GET_SALON_LIST_QUERY,
                                variables: { resolveRole: 'SYSADMIN' },
                                data: {
                                    ...salonList,
                                    viewer: {
                                        ...salonList.viewer,
                                        salonList: [
                                            newSalon,
                                            ...salonList.viewer.salonList,
                                        ],
                                    },
                                },
                            });
                        }
                    }
                },
            });

            setupNewSalon(result.salon.id, () => {
                setOwnerAsMaster(result.salon.id, user, () => {
                    dispatch(setIsOwner(true));
                    dispatch(switchSalon(result.salon.id, result.salon.name));
                    onClose();
                });
            });
        } catch (error) {
            const graphQLError = error.graphQLErrors && error.graphQLErrors[0];
            if (
                graphQLError &&
                graphQLError.name === 'AlreadyExists' &&
                graphQLError.data &&
                get(graphQLError, 'data.error') === 'NAME_ALREADY_EXISTS'
            ) {
                setError('name', {
                    message: intl.formatMessage(intlMessages.nameExistingsError),
                });
                return;
            }
            setError('root', {
                message: intl.formatMessage(intlMessages.defaultErrorMessage),
            });
        }
    };

    return (
        <Modal trigger={trigger} open={open} onClose={onClose} size="small">
            <Modal.Header>
                <FormattedMessage
                    id="navbar.menu.newSalon.header"
                    defaultMessage="Create a new salon"
                />
            </Modal.Header>
            <Modal.Content>
                {errors.root && (
                    <Message error>
                        <Icon name="warning sign" />
                        {errors.root.message}
                    </Message>
                )}
                <Form onSubmit={handleSubmit(onSubmit)}>
                    <Form.Field error={!!errors.name}>
                        <Controller
                            name="name"
                            control={control}
                            rules={{
                                required: intl.formatMessage(
                                    intlMessages.salonNameError,
                                ),
                            }}
                            render={({ field: { ref, ...field } }) => (
                                <Input
                                    {...field}
                                    onChange={(e, data) =>
                                        field.onChange(data?.value ?? e)
                                    }
                                    placeholder={intl.formatMessage(
                                        intlMessages.salonNamePlaceholder,
                                    )}
                                    fluid
                                />
                            )}
                        />
                        {errors.name && (
                            <BaseLabel color="red" pointing basic>
                                {errors.name.message}
                            </BaseLabel>
                        )}
                    </Form.Field>
                    <Form.Field error={!!errors.address}>
                        <Controller
                            name="address"
                            control={control}
                            rules={{
                                required: intl.formatMessage(
                                    intlMessages.salonAddressError,
                                ),
                            }}
                            render={({ field: { ref, ...field } }) => (
                                <Input
                                    {...field}
                                    onChange={(e, data) =>
                                        field.onChange(data?.value ?? e)
                                    }
                                    placeholder={intl.formatMessage(
                                        intlMessages.salonAddressPlaceholder,
                                    )}
                                    fluid
                                />
                            )}
                        />
                        {errors.address && (
                            <BaseLabel color="red" pointing basic>
                                {errors.address.message}
                            </BaseLabel>
                        )}
                    </Form.Field>
                </Form>
            </Modal.Content>
            <Modal.Actions>
                <Button
                    onClick={handleSubmit(onSubmit)}
                    loading={isSubmitting}
                    disabled={!isDirty || isSubmitting}
                    primary
                >
                    <FormattedMessage
                        id="navbar.menu.newSalon.create"
                        defaultMessage="Create"
                    />
                </Button>
                <Button onClick={onClose} disabled={isSubmitting}>
                    <Icon name="remove" />
                    <FormattedMessage
                        id="navbar.menu.newSalon.cancel"
                        defaultMessage="Cancel"
                    />
                </Button>
            </Modal.Actions>
        </Modal>
    );
};

export default injectIntl(withNewSalon(CreateSalonDialog));
